// src/components/operationCenter/widgets/SectorNarrativesWidget.tsx
// Sector narratives widget showing top sector momentum by time range

import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DynamicIcon, type IconName } from "lucide-react/dynamic";
import type { WidgetProps } from "@/types/dashboard";
import { useLeafCategorySnapshots } from "@/hooks/dashboard/useLeafCategorySnapshots";
import { useCategoriesTree } from "@/hooks/dashboard/useCategoriesTree";
import { TIME_WINDOWS } from "@/constants/metrics";
import { getIconForLabel } from "@/config/icons";
import TimeRangePicker from "@/components/common/TimeRangePicker";
import { WIDGET_EMPTY } from "../WidgetWrapper";

function nz(n: number | null | undefined): number {
  return Number.isFinite(n as number) ? (n as number) : 0;
}

type RangeKey = "1d" | "7d" | "30d" | "365d";

const SectorNarrativesWidget: React.FC<WidgetProps> = () => {
  const { leaves, isLoading } = useLeafCategorySnapshots();
  const { maps } = useCategoriesTree();
  const navigate = useNavigate();

  const [range, setRange] = useState<RangeKey>("7d");
  const windowKey = useMemo(
    () => TIME_WINDOWS.UI_TO_API[range] || "7d",
    [range]
  );

  const topSectors = useMemo(() => {
    const clean = leaves.filter(
      (leaf) => (leaf.snap.current?.["market_cap"] || 0) > 0 && leaf.code !== "overview"
    );

    const scored = clean.map((leaf) => {
      const mcapChange = nz((leaf.snap.changes?.["market_cap"]?.[windowKey]?.value || 0) * 100);
      const volumeChange = nz((leaf.snap.changes?.["volume"]?.[windowKey]?.value || 0) * 100);
      return {
        code: leaf.code,
        name: leaf.name,
        score: mcapChange * 0.7 + volumeChange * 0.3,
        mcapChange,
        volumeChange,
      };
    });

    return scored.sort((a, b) => b.score - a.score).slice(0, 10);
  }, [leaves, windowKey]);

  const buildRouteFromCodes = (codes: string[]): string => {
    if (!codes || codes.length === 0) return "/app/sectors/overall";
    const [root, ...rest] = codes;
    if (root === "overview") return "/app/sectors/overall";
    if (root === "bitcoin") return "/app/tokens/btc";
    const base = `/app/sectors/${root}`;
    if (rest.length === 0) return base;
    return `${base}/${rest.join("/")}`;
  };

  const buildCodeTrail = (code: string): string[] => {
    const trail: string[] = [];
    let current: string | null | undefined = code;
    while (current) {
      trail.push(current);
      current = maps.parentByCode[current];
    }
    return trail.reverse();
  };

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-pulse text-slate-500 text-sm">Loading...</div>
      </div>
    );
  }

  if (topSectors.length === 0) {
    return (
      <div className={WIDGET_EMPTY}>
        No sector narratives available
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-700/30">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-300 font-medium">
          <DynamicIcon name="flame" className="w-3 h-3 text-amber-400" />
          Hot Sectors
        </div>
        <TimeRangePicker
          options={["1d", "7d", "30d", "365d"] as const}
          value={range}
          onChange={setRange}
        />
      </div>

      <div className="flex-1 overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-slate-900/80 text-slate-400">
            <tr className="border-b border-slate-800/60">
              <th className="text-left font-medium px-3 py-2">Sector</th>
              <th className="text-right font-medium px-3 py-2">{range} MCap</th>
              <th className="text-right font-medium px-3 py-2">{range} Vol</th>
            </tr>
          </thead>
          <tbody>
            {topSectors.map((sector, idx) => {
              const iconName = getIconForLabel(sector.name) as IconName;
              const href = buildRouteFromCodes(buildCodeTrail(sector.code));
              const mcapPositive = sector.mcapChange >= 0;
              const volPositive = sector.volumeChange >= 0;

              return (
                <tr
                  key={sector.code}
                  className="border-b border-slate-800/40 hover:bg-slate-800/40 transition-colors"
                >
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      onClick={() => navigate(href)}
                      className="flex items-center gap-2 text-left group"
                    >
                      <span className="text-[10px] text-slate-500 w-4 tabular-nums">
                        {idx + 1}
                      </span>
                      <DynamicIcon
                        name={iconName}
                        className="w-4 h-4 text-slate-400 group-hover:text-slate-200 transition-colors"
                      />
                      <span className="text-slate-200 font-medium group-hover:text-white transition-colors">
                        {sector.name}
                      </span>
                    </button>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    <span className={mcapPositive ? "text-emerald-400" : "text-rose-400"}>
                      {mcapPositive ? "+" : ""}
                      {sector.mcapChange.toFixed(2)}%
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    <span className={volPositive ? "text-emerald-400" : "text-rose-400"}>
                      {volPositive ? "+" : ""}
                      {sector.volumeChange.toFixed(2)}%
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SectorNarrativesWidget;
